// src/utils/danfse.js
//
// DANFSe (Documento Auxiliar da NFS-e) gerado a partir do XML oficial da NFS-e Nacional.
// A API nacional de PDF está fora do ar; o documento é montado aqui e impresso/salvo em PDF pelo navegador.
// O DANFSe é um documento auxiliar: o documento fiscal válido é o XML.

import QRCode from 'qrcode';
import { openPrintDocument, gerarPdfDoLayout, escapeHtml, fmtBRL } from './printLayout';

const CONSULTA_PUBLICA = 'https://www.nfse.gov.br/consultapublica';

const onlyDigits = (v) => String(v || '').replace(/\D/g, '');
const fmtDoc = (v) => {
    const d = onlyDigits(v);
    if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    return v || '';
};
const fmtCep = (v) => { const d = onlyDigits(v); return d.length === 8 ? d.replace(/^(\d{5})(\d{3})$/, '$1-$2') : (v || ''); };
const fmtFone = (v) => {
    const d = onlyDigits(v);
    if (d.length === 11) return d.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    if (d.length === 10) return d.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    if (d.length === 8) return d.replace(/^(\d{4})(\d{4})$/, '$1-$2');
    return v || '';
};
const fmtDateTimeIso = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return v;
    return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
};
const fmtDateIso = (v) => (v && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10).split('-').reverse().join('/') : (v || ''));
const fmtChave = (c) => onlyDigits(c).replace(/(\d{4})(?=\d)/g, '$1 ');

/** Lê o XML (com namespace padrão) e devolve um acessor simples por caminho de tags. */
function parse(xmlString) {
    const doc = new DOMParser().parseFromString(xmlString, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length) throw new Error('XML da NFS-e inválido.');
    const first = (root, name) => (root ? root.getElementsByTagName(name)[0] : null);
    const text = (root, ...path) => {
        let el = root;
        for (const p of path) { el = first(el, p); if (!el) return ''; }
        return (el.textContent || '').trim();
    };
    return { doc, first, text };
}

function endereco(text, el) {
    if (!el) return '';
    const lgr = text(el, 'xLgr');
    const nro = text(el, 'nro');
    const cpl = text(el, 'xCpl');
    const bairro = text(el, 'xBairro');
    return [lgr && `${lgr}${nro ? `, ${nro}` : ''}${cpl ? ` - ${cpl}` : ''}`, bairro].filter(Boolean).join(' · ');
}

const ISS_RETENCAO = { '1': 'Não retido', '2': 'Retido pelo tomador', '3': 'Retido pelo intermediário' };
const ISS_TRIBUTACAO = { '1': 'Operação tributável', '2': 'Imunidade', '3': 'Exportação de serviço', '4': 'Não incidência' };
const SIMPLES = { '1': 'Não optante', '2': 'Optante — MEI', '3': 'Optante — ME/EPP' };

/** Monta os parâmetros do layout do DANFSe a partir do XML. */
async function montarDanfse(xmlString, { emittedBy = '' } = {}) {
    const { doc, first, text } = parse(xmlString);
    const inf = first(doc, 'infNFSe');
    const dps = first(inf, 'infDPS');
    const emit = first(inf, 'emit');
    const prest = first(dps, 'prest');
    const toma = first(dps, 'toma');
    const serv = first(dps, 'serv');
    const valDps = first(dps, 'valores');
    const valNfse = (() => { // <valores> direto em infNFSe (não o do DPS)
        const all = inf ? Array.from(inf.children).filter(c => c.localName === 'valores') : [];
        return all[0] || null;
    })();

    const chave = onlyDigits((inf && inf.getAttribute('Id')) || '').slice(-50);
    const numero = text(inf, 'nNFSe');
    const urlConsulta = `${CONSULTA_PUBLICA}?chave=${chave}`;
    let qr = '';
    try { qr = await QRCode.toDataURL(urlConsulta, { margin: 1, width: 220, errorCorrectionLevel: 'M' }); } catch (e) { qr = ''; }

    const v = (name) => Number(text(valDps, name) || text(valNfse, name) || 0);
    const vServ = v('vServ');
    const vDescInc = v('vDescIncond');
    const vDescCond = v('vDescCond');
    const vDed = v('vDR');
    const vBC = Number(text(valNfse, 'vBC') || 0);
    const pAliq = Number(text(valNfse, 'pAliqAplic') || text(valDps, 'pAliq') || 0);
    const vISS = Number(text(valNfse, 'vISSQN') || 0);
    const vTotRet = Number(text(valNfse, 'vTotalRet') || 0);
    const vLiq = Number(text(valNfse, 'vLiq') || 0) || (vServ - vDescInc - vDescCond - vTotRet);
    const retIRRF = v('vRetIRRF');
    const retCSLL = v('vRetCSLL');
    const retCP = v('vRetCP');
    const vPis = v('vPis');
    const vCofins = v('vCofins');
    const tpRet = text(valDps, 'tpRetISSQN');
    const tribISS = text(valDps, 'tribISSQN');
    const pTotFed = text(valDps, 'pTotTribFed');
    const pTotEst = text(valDps, 'pTotTribEst');
    const pTotMun = text(valDps, 'pTotTribMun');

    const tomaDoc = text(toma, 'CNPJ') || text(toma, 'CPF') || text(toma, 'NIF');
    const tomaEnd = first(toma, 'end');
    const tomaCep = text(tomaEnd, 'CEP');

    const emitEnd = first(emit, 'enderNac');
    const pct = (n) => `${Number(n).toFixed(2).replace('.', ',')}%`;
    const cell = (label, value, cls = '') => `<div class="cell ${cls}"><span class="label">${label}</span><span class="value">${value || '<span class="muted">—</span>'}</span></div>`;
    const money = (n) => fmtBRL(n);
    // Só mostra tributos/retenções com valor (os demais ficam de fora para caber em uma página)
    const tribCells = [
        cell('Tributação do ISSQN', escapeHtml(ISS_TRIBUTACAO[tribISS] || '')),
        cell('Retenção do ISSQN', escapeHtml(ISS_RETENCAO[tpRet] || '')),
        cell('Município de incidência', escapeHtml(text(inf, 'xLocIncid'))),
        cell('Tributos aprox. (Fed/Est/Mun)', [pTotFed, pTotEst, pTotMun].some(x => x !== '') ? `${pTotFed || 0}% / ${pTotEst || 0}% / ${pTotMun || 0}%` : ''),
        vBC ? cell('Base de cálculo', money(vBC)) : '',
        pAliq ? cell('Alíquota ISSQN', pct(pAliq)) : '',
        vISS ? cell('Valor do ISSQN', money(vISS)) : '',
        retIRRF ? cell('IRRF retido', money(retIRRF)) : '',
        retCSLL ? cell('CSLL retida', money(retCSLL)) : '',
        retCP ? cell('Contrib. previdenciária retida', money(retCP)) : '',
        vPis ? cell('PIS', money(vPis)) : '',
        vCofins ? cell('COFINS', money(vCofins)) : ''
    ].filter(Boolean);

    const bodyHtml = `
        <div class="nf-top">
            <div class="nf-id">
                <div class="nf-num">NFS-e nº <b>${escapeHtml(numero)}</b></div>
                <div class="nf-meta">
                    <span>Emissão: <b>${escapeHtml(fmtDateTimeIso(text(dps, 'dhEmi') || text(inf, 'dhProc')))}</b></span>
                    <span>Competência: <b>${escapeHtml(fmtDateIso(text(dps, 'dCompet')))}</b></span>
                    <span>DPS nº <b>${escapeHtml(text(dps, 'nDPS'))}</b> · série <b>${escapeHtml(text(dps, 'serie'))}</b></span>
                    <span>Município emissor: <b>${escapeHtml(text(inf, 'xLocEmi'))}</b></span>
                </div>
                <div class="nf-chave"><span class="label">Chave de acesso</span><span class="mono">${escapeHtml(fmtChave(chave))}</span></div>
            </div>
            <div class="nf-qr">
                ${qr ? `<img src="${qr}" alt="QR Code de consulta" />` : ''}
                <span>Consulte a autenticidade em<br/><b>nfse.gov.br/consultapublica</b></span>
            </div>
        </div>

        <div class="section">
            <h3 class="section-title">Prestador do serviço</h3>
            <div class="info-grid">
                ${cell('Nome / Razão social', escapeHtml(text(emit, 'xNome')), 'wide2')}
                ${cell('CNPJ / CPF', escapeHtml(fmtDoc(text(emit, 'CNPJ') || text(emit, 'CPF'))))}
                ${cell('Endereço', escapeHtml(endereco(text, emitEnd)), 'wide2')}
                ${cell('Município / CEP', escapeHtml([text(inf, 'xLocEmi'), text(emitEnd, 'UF')].filter(Boolean).join(' / ') + (text(emitEnd, 'CEP') ? ` · ${fmtCep(text(emitEnd, 'CEP'))}` : '')))}
                ${cell('Telefone', escapeHtml(fmtFone(text(prest, 'fone') || text(emit, 'fone'))))}
                ${cell('E-mail', escapeHtml((text(prest, 'email') || text(emit, 'email')).toLowerCase()))}
                ${cell('Simples Nacional', escapeHtml(SIMPLES[text(prest, 'opSimpNac')] || ''))}
            </div>
        </div>

        <div class="section">
            <h3 class="section-title">Tomador do serviço</h3>
            <div class="info-grid">
                ${cell('Nome / Razão social', escapeHtml(text(toma, 'xNome')), 'wide2')}
                ${cell('CNPJ / CPF', escapeHtml(fmtDoc(tomaDoc)))}
                ${cell('Endereço', escapeHtml(endereco(text, tomaEnd)), 'wide2')}
                ${cell('CEP', escapeHtml(fmtCep(tomaCep)))}
                ${cell('Telefone', escapeHtml(fmtFone(text(toma, 'fone'))))}
                ${cell('E-mail', escapeHtml(text(toma, 'email').toLowerCase()), 'wide2')}
            </div>
        </div>

        <div class="section">
            <h3 class="section-title">Serviço prestado</h3>
            <div class="info-grid">
                ${cell('Código de tributação nacional', escapeHtml([text(serv, 'cTribNac'), text(inf, 'xTribNac')].filter(Boolean).join(' — ')), 'wide2')}
                ${cell('Local da prestação', escapeHtml(text(inf, 'xLocPrestacao')))}
                ${cell('NBS', escapeHtml([text(serv, 'cNBS'), text(inf, 'xNBS')].filter(Boolean).join(' — ')), 'wide3')}
            </div>
            <div class="box" style="margin-top:8px"><span class="label">Descrição do serviço</span>${escapeHtml(text(serv, 'xDescServ')).replace(/\n/g, '<br/>')}</div>
        </div>

        <div class="section">
            <h3 class="section-title">Tributação e valores</h3>
            <div class="nf-values">
                <div class="info-grid grid4">
                    ${tribCells.join('')}
                </div>
                <div class="totals" style="width:100%;margin-top:0">
                    <div class="row"><span>Valor do serviço</span><span class="mono">${money(vServ)}</span></div>
                    ${vDescInc ? `<div class="row"><span>Desconto incondicionado</span><span class="mono">- ${money(vDescInc)}</span></div>` : ''}
                    ${vDescCond ? `<div class="row"><span>Desconto condicionado</span><span class="mono">- ${money(vDescCond)}</span></div>` : ''}
                    ${vDed ? `<div class="row"><span>Deduções / reduções</span><span class="mono">- ${money(vDed)}</span></div>` : ''}
                    ${vTotRet ? `<div class="row"><span>Total de retenções</span><span class="mono">- ${money(vTotRet)}</span></div>` : ''}
                    <div class="row grand"><span>Valor líquido</span><span class="mono">${money(vLiq)}</span></div>
                </div>
            </div>
        </div>

        <p class="muted" style="font-size:7.5pt;margin-top:14px">
            Documento auxiliar gerado pela Intranet Arcus a partir do XML oficial da NFS-e (Ambiente Nacional).
            Não substitui o arquivo XML, que é o documento fiscal válido.
        </p>`;

    const extraCss = `
        .nf-top { display: grid; grid-template-columns: 1fr 150px; gap: 14px; align-items: stretch; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; }
        .nf-num { font-size: 15pt; font-weight: 800; color: #0f172a; }
        .nf-num b { color: #0891b2; }
        .nf-meta { display: flex; flex-wrap: wrap; gap: 4px 16px; margin-top: 4px; font-size: 8.5pt; color: #475569; }
        .nf-meta b { color: #0f172a; }
        .nf-chave { margin-top: 10px; padding-top: 8px; border-top: 1px dashed #cbd5e1; }
        .nf-chave .mono { font-size: 10pt; font-weight: 700; letter-spacing: .5px; color: #0f172a; }
        .nf-qr { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; font-size: 7pt; color: #64748b; }
        .nf-qr img { width: 118px; height: 118px; }
        .info-grid .cell.wide2 { grid-column: span 2; }
        .info-grid .cell.wide2 + .cell { border-right: 0; }
        .info-grid .cell.wide3 { grid-column: span 3; border-right: 0; }
        .nf-values { display: grid; grid-template-columns: 1fr 220px; gap: 12px; align-items: start; }
        .info-grid.grid4 { grid-template-columns: repeat(2, 1fr); }
        .info-grid.grid4 .cell { border-right: 1px solid #e2e8f0 !important; }
        .info-grid.grid4 .cell:nth-child(2n) { border-right: 0 !important; }
        .info-grid .cell { padding: 6px 10px; }
        .section { margin-top: 12px; }
        .doc-meta { margin-bottom: 10px; }
        body { font-size: 9pt; line-height: 1.35; }
        .doc-header img { height: 38px; }
        .doc-title h1 { font-size: 15pt; }
        .label { font-size: 6.5pt; }
        .section-title { margin-bottom: 6px; }
        .nf-top { padding: 10px 12px; grid-template-columns: 1fr 120px; }
        .nf-qr img { width: 96px; height: 96px; }
        .box { padding: 8px 12px; }
        .doc-footer { margin-top: 14px; }
    `;

    return {
        docTitle: `DANFSe ${numero} - ${text(toma, 'xNome')}`.slice(0, 120),
        kicker: 'Documento Auxiliar da NFS-e',
        title: `DANFSe nº ${numero}`,
        subtitle: text(toma, 'xNome'),
        bodyHtml,
        footerNote: `NFS-e ${numero} · chave ${chave}`,
        extraCss,
        emittedBy
    };
}

/**
 * Abre o DANFSe para impressão / salvar em PDF.
 * @param {string} xmlString XML completo da NFS-e
 * @param {object} [opts] { emittedBy }
 */
export async function printDanfse(xmlString, opts = {}) {
    return openPrintDocument(await montarDanfse(xmlString, opts));
}

/** Gera o PDF do DANFSe (mesmo layout da impressão) como Blob, sem abrir janela. */
export async function gerarDanfsePdf(xmlString, opts = {}) {
    return gerarPdfDoLayout(await montarDanfse(xmlString, opts));
}

/** Baixa o XML como arquivo. */
export function downloadXml(xmlString, chave) {
    const blob = new Blob([xmlString], { type: 'application/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `NFSe_${chave || 'nota'}.xml`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
}
